import React, { useState, useEffect, useRef } from "react";
import { 
  FiSearch, FiFilter, FiMoreVertical, FiTrendingUp,
  FiClock, FiAlertCircle, FiCheckCircle, FiBookOpen,
  FiMessageSquare, FiCalendar, FiChevronDown, FiPlus, 
  FiUsers, FiCheckSquare, FiAward, FiUpload, FiLayers
} from "react-icons/fi";
import ChangePasswordModal from "../account/ChangePasswordModal";

import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import api from "../connector/api";

function TeacherDashboard() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [user, setUser] = useState({ first_name: "Teacher", last_name: "User", email: "teacher@school.edu" });
  const [notifOpen, setNotifOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [studentSearch, setStudentSearch] = useState("");
  const [activeTab, setActiveTab] = useState("Today");

  const [currentPeriod, setCurrentPeriod] = useState(null);
  const [loadingPeriod, setLoadingPeriod] = useState(true);

  // Dynamic Dashboard Stats State
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);

  const notificationDropdownRef = useRef(null);

  // Format Today's Date
  const todayDate = new Date().toLocaleDateString("en-US", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric"
  });

  useEffect(() => {
    const storedPerms = sessionStorage.getItem("permissions");
    const storedUser = sessionStorage.getItem("user");
    
    if (storedPerms) {
      try {
        setPermissions(JSON.parse(storedPerms));
      } catch (err) {
        console.error("Failed to parse permissions:", err);
      }
    }

    if (storedUser) {
      try {
        const userObj = JSON.parse(storedUser);
        setUser({
          first_name: userObj.first_name || "Teacher",
          last_name: userObj.last_name || "User",
          username: userObj.username || "teacher",
          email: userObj.email || "teacher@school.edu"
        });
      } catch (err) {
        console.error("Failed to parse user session:", err);
      }
    }

    const fetchCurrentPeriod = async () => {
      try {
        const res = await api.get("year-terms/get-set-year-term/");
        setCurrentPeriod(res.data);
      } catch (err) {
        console.error("Failed to fetch active school year-term:", err);
      } finally {
        setLoadingPeriod(false);
      }
    };

    const fetchTeacherDashboardStats = async () => {
      try {
        const res = await api.get("teacher/dashboard/stats/");
        setDashboardStats(res.data);
      } catch (err) {
        console.error("Failed to fetch teacher dashboard stats:", err);
      } finally {
        setLoadingStats(false);
      }
    };

    fetchCurrentPeriod();
    fetchTeacherDashboardStats();
  }, []);

  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();
    window.location.href = "/login";
  };

  // Safe String Extractions
  const yearDisplay = 
    currentPeriod?.school_year_detail?.name || 
    (typeof currentPeriod?.school_year === 'object' ? currentPeriod?.school_year?.name : currentPeriod?.school_year) || 
    "2025/2026";

  const termDisplay = 
    currentPeriod?.term_detail?.name || 
    (typeof currentPeriod?.term === 'object' ? currentPeriod?.term?.name : currentPeriod?.term) || 
    "Term 1";

  // Dynamic Metrics Data with Safe Fallback Values
  const metrics = dashboardStats?.metrics || { 
    assigned_students: 42, 
    pending_grading: 12, 
    class_average: 88.5 
  };

  const assignedClassesList = dashboardStats?.classes_preview || [
    { id: 1, name: "Grade 10 - Mathematics", room: "Room 102", schedule: "08:00 - 09:30 AM", students: 24 },
    { id: 2, name: "Grade 11 - Physics", room: "Lab 3", schedule: "10:00 - 11:30 AM", students: 18 },
    { id: 3, name: "Grade 10 - Advanced Algebra", room: "Room 105", schedule: "01:00 - 02:30 PM", students: 20 }
  ];

  const totalClassesCount = assignedClassesList.length;

  const attendanceGraphData = dashboardStats?.attendance_graph?.data || [
    { label: "Mon", rate: 95 },
    { label: "Tue", rate: 92 },
    { label: "Wed", rate: 98 },
    { label: "Thu", rate: 94 },
    { label: "Fri", rate: 91 }
  ];

  const pendingSubmissionsList = dashboardStats?.pending_submissions || [
    { id: "SUB-01", name: "Sarah Jenkins", assignment: "Algebra Homework #4", class: "Grade 10 - Math", date: "Today, 08:30 AM", status: "Needs Grading" },
    { id: "SUB-02", name: "Michael Chang", assignment: "Physics Lab Report", class: "Grade 11 - Physics", date: "Yesterday", status: "Needs Grading" },
    { id: "SUB-03", name: "David Kim", assignment: "Polynomial Quiz", class: "Grade 10 - Math", date: "Yesterday", status: "Late Submission" },
    { id: "SUB-04", name: "Emma Watson", assignment: "Algebra Homework #4", class: "Grade 10 - Math", date: "2 days ago", status: "Needs Grading" }
  ];

  // Filter Submissions
  const filteredSubmissions = pendingSubmissionsList.filter(sub => {
    const nameMatch = sub?.name ? sub.name.toLowerCase().includes(studentSearch.toLowerCase()) : false;
    const assignmentMatch = sub?.assignment ? sub.assignment.toLowerCase().includes(studentSearch.toLowerCase()) : false;
    return nameMatch || assignmentMatch;
  });

  const activities = [
    { time: "08:45 AM", title: "Assignment Submitted", desc: "Sarah Jenkins submitted Algebra HW #4", icon: <FiUpload className="w-3 h-3 text-blue-600" /> },
    { time: "08:15 AM", title: "Attendance Marked", desc: "Grade 10 Math attendance updated", icon: <FiCheckCircle className="w-3 h-3 text-emerald-600" /> },
    { time: "07:50 AM", title: "Grade Posted", desc: "Physics Midterm scores released", icon: <FiAward className="w-3 h-3 text-indigo-600" /> },
    { time: "Yesterday", title: "Absence Notice", desc: "Parent note submitted for Alex Turner", icon: <FiAlertCircle className="w-3 h-3 text-rose-600" /> },
    { time: "Yesterday", title: "Syllabus Update", desc: "Uploaded Chapter 5 reference reading", icon: <FiBookOpen className="w-3 h-3 text-cyan-600" /> },
    { time: "2 days ago", title: "Parent Message", desc: "Inquiry received regarding Grade 11 lab", icon: <FiMessageSquare className="w-3 h-3 text-amber-600" /> },
  ];

  return (
    <div className="h-screen w-screen bg-[#f8f9fa] flex font-dynamic-title text-zinc-700 antialiased overflow-hidden selection:bg-zinc-900 selection:text-white">
      
      {/* Sidebar Component */}
      <Sidebar
        permissions={permissions}
        user={user}
        teacherSubjects={assignedClassesList}
        handleLogout={handleLogout}
        onOpenChangePassword={() => setIsModalOpen(true)}
      />

      {/* Main Workspace */}
      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
        
        {/* Header */}
        <Header
            loading={loadingPeriod}
            loadingPeriod={loadingPeriod}
            yearDisplay={yearDisplay}
            termDisplay={termDisplay}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            notifOpen={notifOpen}
            setNotifOpen={setNotifOpen}
            notificationDropdownRef={notificationDropdownRef}
        />

        {/* Dashboard Content Workspace */}
        <main className="p-3 md:p-4 flex-1 flex flex-col gap-3 min-h-0 overflow-hidden">
          
          {/* Greeting Banner */}
          <div className="flex items-center justify-between shrink-0">
            <div>
              <h1 className="text-base md:text-lg font-bold text-zinc-900 tracking-tight flex items-center gap-2">
                Good Morning, {user.first_name || "Teacher"} 👋
              </h1>
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-semibold text-zinc-600 bg-white border border-zinc-200 px-2.5 py-1 rounded-lg shadow-2xs">
                📅 {todayDate}
              </span>
              <button className="flex items-center gap-1 text-[11px] font-medium bg-white border border-zinc-200 px-2.5 py-1 rounded-lg shadow-2xs hover:bg-zinc-50 transition-colors">
                <FiCalendar className="w-3 h-3 text-zinc-500" />
                <span>{termDisplay}</span>
                <FiChevronDown className="w-3 h-3 text-zinc-400" />
              </button>
            </div>
          </div>
        </main>
      </div>
      {isModalOpen && <ChangePasswordModal onClose={() => setIsModalOpen(false)} />}
    </div>
  );
}

export default TeacherDashboard;